import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StatusChip from '../../changelog/components/StatusChip';
import { TEMPLATE_ENABLED, labelOf, withPercent } from '../onboardingAdminConfig';

const headSx = { fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' };

const buttonLabel = (template, targets) => {
  if (template.plain) return 'Texto simples';
  return template.button_label ? labelOf(targets, template.button_target) : '—';
};

/**
 * Modelos de uma etapa da jornada (groupTemplatesByStage), com o código e os números de cada variante.
 * Cliques em destaque: é o número que mostra se o e-mail ajudou.
 * @param {{ group: Object, targets: Array, onEdit: Function, onPreview: Function }} props
 */
function TemplatesTable({ group, targets, onEdit, onPreview }) {
  const { templates } = group;
  return (
    <Paper elevation={0} sx={{ mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {group.code && <Box component="span" sx={{ color: 'primary.main', mr: 1 }}>{group.code}</Box>}
          {group.label}
        </Typography>
        {group.hint && <Typography variant="caption" color="text.secondary">{group.hint}</Typography>}
      </Box>

      {templates.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 2, md: 2.5 }, pb: 2 }}>
          {group.emptyText}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Código</TableCell>
                <TableCell sx={headSx}>E-mail</TableCell>
                <TableCell sx={headSx}>Botão</TableCell>
                <TableCell sx={headSx} align="right">Enviados</TableCell>
                <TableCell sx={headSx} align="right">Aberturas</TableCell>
                <TableCell sx={{ ...headSx, color: 'primary.main' }} align="right">Cliques</TableCell>
                <TableCell sx={headSx} align="right">Pulados</TableCell>
                <TableCell sx={headSx} align="right">Falhas</TableCell>
                <TableCell sx={headSx} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => {
                const stats = template.stats || {};
                const sent = stats.sent || 0;
                const failed = stats.failed || 0;
                return (
                  <TableRow key={template.key} hover>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{template.variant?.code || '—'}</TableCell>
                    <TableCell sx={{ minWidth: 280 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{template.variant?.label || template.key}</Typography>
                        <StatusChip info={template.enabled ? TEMPLATE_ENABLED.on : TEMPLATE_ENABLED.off} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{template.subject}</Typography>
                      <Typography variant="caption" color="text.disabled">{template.key}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{buttonLabel(template, targets)}</TableCell>
                    <TableCell align="right">{sent}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{withPercent(stats.opened || 0, sent)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}>{withPercent(stats.clicked || 0, sent)}</TableCell>
                    <TableCell align="right">{stats.skipped || 0}</TableCell>
                    <TableCell align="right" sx={{ color: failed > 0 ? 'error.main' : undefined }}>{failed}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Ver prévia">
                        <IconButton size="small" onClick={() => onPreview(template)} aria-label={`Prévia ${template.key}`}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => onEdit(template)} aria-label={`Editar ${template.key}`}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

export default TemplatesTable;
